import type { Category } from '@/lib/api';

interface CategoryFilterProps {
  categories: Category[];
  /** null means every category is shown. */
  selected: ReadonlySet<string> | null;
  onToggle: (slug: string) => void;
  onReset: () => void;
  counts: Record<string, number>;
}

/**
 * Category chips. Rendered as real checkboxes with a visual chip label, so
 * keyboard and screen-reader users get the actual control rather than a div
 * that happens to respond to clicks.
 */
export function CategoryFilter({
  categories,
  selected,
  onToggle,
  onReset,
  counts,
}: CategoryFilterProps) {
  const isOn = (slug: string) => selected === null || selected.has(slug);

  return (
    // On a phone, eight chips wrap to four rows and take a third of the
    // viewport away from the map. One horizontally scrollable row keeps every
    // category reachable without costing the map its height; from `sm` up
    // there is room to wrap them normally.
    <fieldset className="-mx-1 flex snap-x items-center gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-x-visible sm:px-0 sm:pb-0">
      <legend className="sr-only">Filtr kategorií</legend>

      {categories.map((category) => {
        const on = isOn(category.slug);
        return (
          <label
            key={category.slug}
            className={`inline-flex shrink-0 snap-start cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-700 ${
              on
                ? 'border-transparent text-white'
                : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
            }`}
            style={on ? { backgroundColor: category.color } : undefined}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={on}
              onChange={() => onToggle(category.slug)}
            />
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 rounded-full ring-1 ring-white/60"
              style={{ backgroundColor: on ? 'rgba(255,255,255,0.9)' : category.color }}
            />
            {category.name_cs}
            <span className={on ? 'text-white/80' : 'text-slate-400'}>
              {counts[category.slug] ?? 0}
            </span>
          </label>
        );
      })}

      {selected !== null && (
        <button
          type="button"
          onClick={onReset}
          className="shrink-0 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          Zobrazit vše
        </button>
      )}
    </fieldset>
  );
}
